<template>
  <div v-show="visible" class="tab-pane-content">
    <!-- Action Button -->
    <div class="action-buttons">
      <Button 
        data-testid="novo-cliente-btn"
        severity="primary"
        icon="pi pi-plus"
        :label="t('Novo cliente')"
        @click="handleNovoCliente"
      />
    </div>

    <!-- Table -->
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :auto-load="false"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="(e: any) => handleRowClick(e)"
      @edit-click="handleEditClick"
      @button-click="handleGridButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { whatsappNumber } from '../../composables/usePhoneFormat';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface TabProps {
  visible?: boolean;
}

const props = withDefaults(defineProps<TabProps>(), {
  visible: false
});

const emit = defineEmits<{
  'update:total': [total: number];
}>();

const { t } = useI18n();

const dataTableRef = ref<DataTableInstance | null>(null);
const totalRecords = ref(0);

// Computed
const endpoint = computed(() => '/index.php/gestao_clientes/carrega_clientes_sem_orcamento');

// DataTable Configuration
const tableConfig = computed(() => {
  return {
    tableId: 'tabelaClientesSemOrcamento',
    title: t('Clientes sem Orçamento'),
    filename: `clientes_sem_orcamento_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pagination: true,
    pageLength: 25,
    heightMode: 'fill-viewport',
    rowSelection: 'single',
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.cliente_id;
      return id == null ? '' : `cliente-sem-orcamento-row-${id}`;
    },
  };
});

const filterConfig = computed(() => {
  return {
    columns: {
      'cliente_id': 'text',
      'franquia': 'select',
      'nome': 'text',
      'bairro': 'text',
      'localidade': 'select',
      'ultimaOcorrencia': 'text',
      'telefone': 'text'
    }
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'cliente_id',
      title: 'ID',
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (value: Id | undefined, row: { cliente_id?: Id } | undefined) => {
        if (value) {
          eventBus.emit('cliente.edit', value);
        } else if (row?.cliente_id) {
          eventBus.emit('cliente.edit', row.cliente_id);
        }
      },
      dataType: 'number',
    },
    {
      data: 'franquia',
      title: t('Franquia', 'Franquia'),
      width: 150,
      resizable: true,
      sortable: true,
    },
    {
      data: 'nome',
      title: t('Nome', 'Nome'),
      width: 220,
      resizable: true,
      sortable: true,
    },
    {
      data: 'bairro',
      title: t('Bairro', 'Bairro'),
      width: 140,
      resizable: true,
      sortable: true,
    },
    {
      data: 'localidade',
      title: t('Localidade', 'Localidade'),
      width: 160,
      resizable: true,
      sortable: true,
    },
    {
      data: 'ultimaOcorrencia',
      title: t('Ultima-ocorrencia', 'Última ocorrência'),
      width: 300,
      resizable: true,
      sortable: true,
    },
    {
      data: 'telefone',
      title: 'WhatsApp',
      width: 180,
      resizable: true,
      cellRenderer: (params: CellParams) => {
        const row = params.data as { status?: number; telefone?: string; ddd?: string; ddi?: string };

        // O número completo vem de uma regra só (usePhoneFormat): país sem DDD também ganha botão.
        const numero = whatsappNumber(row);
        if (row.status == 0 || numero === null) {
          return '';
        }

        const link = document.createElement('a');
        link.className = 'p-button p-button-success p-button-sm btn-whatsapp';
        link.href = `https://api.whatsapp.com/send?phone=${numero}`;
        link.target = '_blank';
        link.title = 'WhatsApp';
        link.innerHTML = `<i class="pi pi-whatsapp" aria-hidden="true"></i> +${numero}`;

        return link;
      }
    },
    {
      data: 'cliente_id',
      title: t('Orcamento', 'Orçamento'),
      width: 160,
      renderType: 'clickable_button',
      buttonClass: 'p-button p-button-warning p-button-sm btn-adicionar-orcamento',
      buttonText: t('Criar orçamento'),
      buttonIcon: 'pi pi-plus',
      buttonAction: 'novo-orcamento'
    }
  ];
});

const loadData = (): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'loadData', 'Triggering grid refresh');
  dataTableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  totalRecords.value = data.length;
  emit('update:total', data.length);
};

const handleDataError = (err: { message?: string } | Error): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'handleDataError', 'Error loading data', err);
  const message = err instanceof Error ? err.message : err.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar clientes sem orçamento: ') + message);
};

const handleNovoCliente = (): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'handleNovoCliente', 'Opening new cliente modal');
  eventBus.emit('cliente.edit', 0);
};

const handleNovoOrcamento = (clienteId?: Id): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'handleNovoOrcamento', 'Opening new orcamento modal', clienteId);
  eventBus.emit('orcamento.newForClientRequested', clienteId);
  eventBus.emit('orcamento.openNewModalForClient', { clienteId });
  eventBus.emit('orcamento.open-modal-for-client', { clienteId });
};

const handleGridButtonClick = ({ value, data }: { value?: Id; data?: { cliente_id?: Id } }): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'handleGridButtonClick', 'Button clicked', { value });
  handleNovoOrcamento(value || data?.cliente_id);
};

const handleEditClick = (clienteId?: Id): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'handleEditClick', 'Edit clicked', clienteId);
  if (clienteId) {
    eventBus.emit('cliente.edit', clienteId);
  }
};

const handleRowClick = (rowData?: { cliente_id?: Id }): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'handleRowClick', 'Row clicked', rowData);

  const clienteId = rowData?.cliente_id;

  if (clienteId) {
    eventBus.emit('cliente.edit', clienteId);
  } else {
    log('GestaoClientesTabClientesSemOrcamento', 'handleRowClick', 'No cliente_id found in row data', rowData);
  }
};

const handleClienteSaved = (): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'event:cliente.saved', 'Received');
  if (props.visible) {
    loadData();
  }
};

const handleOrcamentoSaved = (): void => {
  log('GestaoClientesTabClientesSemOrcamento', 'event:orcamento.saved', 'Received');
  if (props.visible) {
    loadData();
  }
};

onMounted(() => {
  log('GestaoClientesTabClientesSemOrcamento', 'onMounted', 'Component mounted');

  eventBus.on('cliente.saved', handleClienteSaved);
  eventBus.on('orcamento.saved', handleOrcamentoSaved);
});

onBeforeUnmount(() => {
  log('GestaoClientesTabClientesSemOrcamento', 'onBeforeUnmount', 'Component unmounting');

  eventBus.off('cliente.saved', handleClienteSaved);
  eventBus.off('orcamento.saved', handleOrcamentoSaved);
});

defineExpose({
  loadData
});
</script>

<style scoped>
.tab-pane-content {
  padding: 20px;
}

.action-buttons {
  margin-bottom: 15px;
}

.action-buttons .btn {
  margin-right: 10px;
}

.btn-whatsapp {
  white-space: nowrap;
}

.btn-adicionar-orcamento {
  white-space: nowrap;
}
</style>
